import { useState, useRef, useCallback, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Upload, X, AlertCircle, CheckCircle, Image as ImageIcon } from 'lucide-react';
import { rawFetch } from '../../services/api';

interface BannerUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  slug: string;
  onUploadComplete: (bannerUrl: string, srcset: Record<string, string> | null) => void;
}

interface UploadState {
  status: 'idle' | 'validating' | 'encoding' | 'uploading' | 'confirming' | 'complete' | 'error';
  progress: number;
  error: string | null;
  savedBytes: number | null;
}

const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
const MIN_WIDTH = 1500;
const MIN_HEIGHT = 500;
const MAX_WIDTH = 4000;
const MAX_HEIGHT = 1500;
const WEBP_QUALITY = 0.82;

// Responsive srcset breakpoints - only generate sizes smaller than the original
const SRCSET_WIDTHS = [768, 1200, 1920];

/**
 * Loads an image file into an HTMLImageElement and returns it along with dimensions.
 */
function loadImage(file: File): Promise<{ img: HTMLImageElement; width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const img = new window.Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      resolve({ img, width: img.naturalWidth, height: img.naturalHeight });
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Failed to load image'));
    };
    img.src = url;
  });
}

/**
 * Re-encodes an image to WebP at the given dimensions using Canvas API.
 */
function encodeToWebP(img: HTMLImageElement, width: number, height: number, quality: number): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;

    const ctx = canvas.getContext('2d');
    if (!ctx) {
      reject(new Error('Canvas context not available'));
      return;
    }

    ctx.drawImage(img, 0, 0, width, height);

    canvas.toBlob(
      (blob) => {
        if (blob) {
          resolve(blob);
        } else {
          reject(new Error('Failed to encode image'));
        }
      },
      'image/webp',
      quality
    );
  });
}

interface VariantBlob {
  width: number;
  height: number;
  blob: Blob;
}

/**
 * Generates all responsive variants for the given image.
 * Only generates variants smaller than the original.
 */
async function generateVariants(
  img: HTMLImageElement,
  originalWidth: number,
  originalHeight: number,
  quality: number
): Promise<VariantBlob[]> {
  const variants: VariantBlob[] = [];
  const aspectRatio = originalHeight / originalWidth;

  for (const targetWidth of SRCSET_WIDTHS) {
    // Only generate if target is smaller than original
    if (targetWidth < originalWidth) {
      const targetHeight = Math.round(targetWidth * aspectRatio);
      const blob = await encodeToWebP(img, targetWidth, targetHeight, quality);
      variants.push({ width: targetWidth, height: targetHeight, blob });
    }
  }

  return variants;
}

export default function BannerUploadModal({ isOpen, onClose, slug, onUploadComplete }: BannerUploadModalProps) {
  const [uploadState, setUploadState] = useState<UploadState>({
    status: 'idle',
    progress: 0,
    error: null,
    savedBytes: null,
  });
  const [dragOver, setDragOver] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setUploadState({ status: 'idle', progress: 0, error: null, savedBytes: null });
      setPreviewUrl(null);
      setDragOver(false);
    }
  }, [isOpen]);

  const handleClose = useCallback(() => {
    if (uploadState.status === 'uploading' || uploadState.status === 'confirming' || uploadState.status === 'encoding') {
      return;
    }
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    onClose();
  }, [uploadState.status, onClose]);

  const validateFile = useCallback((file: File): string | null => {
    if (!ALLOWED_TYPES.includes(file.type)) {
      return `Invalid file type. Allowed: JPEG, PNG, WebP`;
    }
    if (file.size > MAX_FILE_SIZE) {
      return `File too large. Maximum size is ${MAX_FILE_SIZE / (1024 * 1024)}MB`;
    }
    return null;
  }, []);

  const uploadFile = useCallback(async (file: File) => {
    // Step 1: Validate locally
    setUploadState({ status: 'validating', progress: 0, error: null, savedBytes: null });

    const validationError = validateFile(file);
    if (validationError) {
      setUploadState({ status: 'error', progress: 0, error: validationError, savedBytes: null });
      return;
    }

    // Load image
    let imgData: { img: HTMLImageElement; width: number; height: number };
    try {
      imgData = await loadImage(file);
    } catch {
      setUploadState({ status: 'error', progress: 0, error: 'Failed to read image. Please try a different file.', savedBytes: null });
      return;
    }

    const { img, width, height } = imgData;

    // Validate dimensions
    if (width < MIN_WIDTH || height < MIN_HEIGHT) {
      setUploadState({
        status: 'error', progress: 0,
        error: `Too small. Image must be at least ${MIN_WIDTH} x ${MIN_HEIGHT} pixels. Your image is ${width} x ${height}.`,
        savedBytes: null,
      });
      URL.revokeObjectURL(img.src);
      return;
    }

    if (width > MAX_WIDTH || height > MAX_HEIGHT) {
      setUploadState({
        status: 'error', progress: 0,
        error: `Too large. Image must be between ${MIN_WIDTH} x ${MIN_HEIGHT} and ${MAX_WIDTH} x ${MAX_HEIGHT} pixels. Your image is ${width} x ${height}.`,
        savedBytes: null,
      });
      URL.revokeObjectURL(img.src);
      return;
    }

    setPreviewUrl(img.src);

    // Step 2: Re-encode original + generate responsive variants
    setUploadState({ status: 'encoding', progress: 3, error: null, savedBytes: null });

    let optimizedBlob: Blob;
    let variants: VariantBlob[];
    try {
      // Encode full-size original
      optimizedBlob = await encodeToWebP(img, width, height, WEBP_QUALITY);

      // If WebP is larger than original (rare), keep original
      if (optimizedBlob.size >= file.size && file.type === 'image/webp') {
        optimizedBlob = file;
      }

      setUploadState({ status: 'encoding', progress: 5, error: null, savedBytes: null });

      // Generate responsive variants
      variants = await generateVariants(img, width, height, WEBP_QUALITY);

      setUploadState({ status: 'encoding', progress: 8, error: null, savedBytes: null });
    } catch {
      setUploadState({ status: 'error', progress: 0, error: 'Failed to optimize image. Please try a different file.', savedBytes: null });
      return;
    }

    const totalOriginalSize = file.size;
    const totalOptimizedSize = optimizedBlob.size + variants.reduce((sum, v) => sum + v.blob.size, 0);
    const savedBytes = totalOriginalSize - optimizedBlob.size; // savings on original only
    const uploadContentType = optimizedBlob.type || 'image/webp';

    // Step 3: Get presigned URLs for all variants
    setUploadState({ status: 'uploading', progress: 10, error: null, savedBytes });

    const variantWidths = variants.map((v) => v.width);

    let presignData: {
      uploadUrl: string;
      path: string;
      variants: Array<{ width: number; uploadUrl: string; path: string }>;
    };
    try {
      const res = await rawFetch('/api/public/tenants/banner/presign', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Tenant-Slug': slug },
        body: JSON.stringify({
          contentType: uploadContentType,
          fileSize: optimizedBlob.size,
          variants: variantWidths,
        }),
      });

      if (!res.ok) {
        const errData = await res.json() as { error: string };
        setUploadState({ status: 'error', progress: 0, error: errData.error || 'Failed to prepare upload', savedBytes: null });
        return;
      }

      presignData = await res.json() as typeof presignData;
    } catch {
      setUploadState({ status: 'error', progress: 0, error: 'Network error. Please check your connection.', savedBytes: null });
      return;
    }

    // Step 4: Upload all files in parallel with aggregate progress
    setUploadState({ status: 'uploading', progress: 12, error: null, savedBytes });

    const totalBytes = optimizedBlob.size + variants.reduce((sum, v) => sum + v.blob.size, 0);
    const loadedMap: Record<string, number> = {};

    const updateAggregateProgress = () => {
      const totalLoaded = Object.values(loadedMap).reduce((sum, v) => sum + v, 0);
      const pct = Math.round(12 + (totalLoaded / totalBytes) * 73);
      setUploadState((prev) => ({ ...prev, progress: Math.min(pct, 85) }));
    };

    const uploadBlob = (url: string, blob: Blob, contentType: string, key: string): Promise<void> => {
      return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();

        xhr.upload.onprogress = (event) => {
          if (event.lengthComputable) {
            loadedMap[key] = event.loaded;
            updateAggregateProgress();
          }
        };

        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            loadedMap[key] = blob.size;
            updateAggregateProgress();
            resolve();
          } else {
            reject(new Error(`Upload failed with status ${xhr.status}`));
          }
        };

        xhr.onerror = () => reject(new Error('Upload failed'));
        xhr.onabort = () => reject(new Error('Upload cancelled'));

        xhr.open('PUT', url);
        xhr.setRequestHeader('Content-Type', contentType);
        xhr.send(blob);
      });
    };

    try {
      // Upload original + all variants in parallel
      const uploads: Promise<void>[] = [
        uploadBlob(presignData.uploadUrl, optimizedBlob, uploadContentType, 'original'),
      ];

      for (let i = 0; i < variants.length; i++) {
        const variant = variants[i];
        const presignVariant = presignData.variants[i];
        if (presignVariant) {
          uploads.push(uploadBlob(presignVariant.uploadUrl, variant.blob, uploadContentType, `variant-${variant.width}`));
        }
      }

      await Promise.all(uploads);
    } catch (err: any) {
      if (err.message === 'Upload cancelled') return;
      setUploadState({ status: 'error', progress: 0, error: 'Upload failed. Please try again.', savedBytes: null });
      return;
    }

    // Step 5: Confirm upload with backend
    setUploadState({ status: 'confirming', progress: 88, error: null, savedBytes });

    try {
      const confirmVariants = presignData.variants.map((v) => ({
        width: v.width,
        path: v.path,
      }));

      const confirmRes = await rawFetch('/api/public/tenants/banner/confirm', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Tenant-Slug': slug },
        body: JSON.stringify({
          path: presignData.path,
          width,
          height,
          variants: confirmVariants,
        }),
      });

      if (!confirmRes.ok) {
        const errData = await confirmRes.json() as { error: string };
        setUploadState({ status: 'error', progress: 0, error: errData.error || 'Failed to process image', savedBytes: null });
        return;
      }

      const confirmData = await confirmRes.json() as { bannerUrl: string; bannerSrcset: Record<string, string> | null };

      // Show 100% for a couple seconds before closing
      setUploadState({ status: 'complete', progress: 100, error: null, savedBytes });

      setTimeout(() => {
        onUploadComplete(confirmData.bannerUrl, confirmData.bannerSrcset);
        onClose();
      }, 2000);
    } catch {
      setUploadState({ status: 'error', progress: 0, error: 'Failed to confirm upload. Please try again.', savedBytes: null });
    }
  }, [slug, validateFile, onUploadComplete, onClose]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) uploadFile(file);
  }, [uploadFile]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
  }, []);

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) uploadFile(file);
  }, [uploadFile]);

  const handleClickUpload = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const isUploading = uploadState.status === 'uploading' || uploadState.status === 'confirming' || uploadState.status === 'validating' || uploadState.status === 'encoding';

  const formatBytes = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] flex items-center justify-center p-4"
        >
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-black/70 backdrop-blur-sm"
            onClick={handleClose}
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ duration: 0.2, ease: 'easeOut' }}
            className="relative w-full max-w-lg bg-[#1A1A1A] border border-white/10 rounded-xl shadow-2xl overflow-hidden"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-white/5">
              <h2 className="text-lg font-semibold text-white">Upload Banner</h2>
              <button
                onClick={handleClose}
                disabled={isUploading}
                className="p-1.5 rounded-lg hover:bg-white/10 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
              >
                <X className="w-5 h-5 text-text-secondary" />
              </button>
            </div>

            {/* Content */}
            <div className="p-6">
              {/* Error message */}
              <AnimatePresence>
                {uploadState.error && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    className="mb-4 overflow-hidden"
                  >
                    <div className="flex items-start gap-3 p-3 rounded-lg bg-red-500/10 border border-red-500/20">
                      <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                      <p className="text-sm text-red-300">{uploadState.error}</p>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Upload area */}
              {uploadState.status === 'idle' || uploadState.status === 'error' ? (
                <div
                  onDrop={handleDrop}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onClick={handleClickUpload}
                  className={`
                    relative flex flex-col items-center justify-center p-10 rounded-xl border-2 border-dashed cursor-pointer transition-all duration-200
                    ${dragOver
                      ? 'border-electric-cyan bg-electric-cyan/5 scale-[1.01]'
                      : 'border-white/20 hover:border-white/40 hover:bg-white/[0.02]'
                    }
                  `}
                >
                  <div className={`w-14 h-14 rounded-full flex items-center justify-center mb-4 transition-colors ${
                    dragOver ? 'bg-electric-cyan/20' : 'bg-white/5'
                  }`}>
                    <Upload className={`w-6 h-6 ${dragOver ? 'text-electric-cyan' : 'text-text-secondary'}`} />
                  </div>

                  <p className="text-sm font-medium text-white mb-1">
                    {dragOver ? 'Drop your image here' : 'Drag & drop your banner image'}
                  </p>
                  <p className="text-xs text-text-muted">
                    or click to select a file
                  </p>

                  <div className="mt-4 flex items-center gap-4 text-xs text-text-muted">
                    <span className="flex items-center gap-1">
                      <ImageIcon className="w-3.5 h-3.5" />
                      JPG, PNG, WebP
                    </span>
                    <span>•</span>
                    <span>{MIN_WIDTH}×{MIN_HEIGHT} – {MAX_WIDTH}×{MAX_HEIGHT}</span>
                    <span>•</span>
                    <span>Max 10MB</span>
                  </div>

                  <p className="mt-3 text-[11px] text-text-muted/60">
                    Auto-optimized to WebP with responsive sizes for all devices
                  </p>

                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".jpg,.jpeg,.png,.webp"
                    onChange={handleFileSelect}
                    className="hidden"
                  />
                </div>
              ) : (
                /* Progress state */
                <div className="space-y-4">
                  {/* Preview */}
                  {previewUrl && (
                    <div className="rounded-lg overflow-hidden border border-white/10 aspect-[3/1]">
                      <img
                        src={previewUrl}
                        alt="Banner preview"
                        className="w-full h-full object-cover"
                      />
                    </div>
                  )}

                  {/* Progress bar */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-text-secondary">
                        {uploadState.status === 'validating' && 'Validating...'}
                        {uploadState.status === 'encoding' && 'Generating responsive sizes...'}
                        {uploadState.status === 'uploading' && 'Uploading all variants...'}
                        {uploadState.status === 'confirming' && 'Processing...'}
                        {uploadState.status === 'complete' && 'Upload complete!'}
                      </span>
                      <span className="text-sm font-mono text-electric-cyan">
                        {uploadState.progress}%
                      </span>
                    </div>

                    <div className="h-2 bg-white/5 rounded-full overflow-hidden">
                      <motion.div
                        className={`h-full rounded-full ${
                          uploadState.status === 'complete'
                            ? 'bg-green-500'
                            : 'bg-electric-cyan'
                        }`}
                        initial={{ width: 0 }}
                        animate={{ width: `${uploadState.progress}%` }}
                        transition={{ duration: 0.3, ease: 'easeOut' }}
                      />
                    </div>

                    {/* Compression savings info */}
                    {uploadState.savedBytes !== null && uploadState.savedBytes > 0 && (
                      <motion.p
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className="text-xs text-text-muted font-mono"
                      >
                        <span className="text-electric-cyan">WebP</span> optimized — saved {formatBytes(uploadState.savedBytes)} + responsive srcset generated
                      </motion.p>
                    )}

                    {uploadState.status === 'complete' && (
                      <motion.div
                        initial={{ opacity: 0, y: 5 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="flex items-center gap-2 text-sm text-green-400"
                      >
                        <CheckCircle className="w-4 h-4" />
                        <span>Banner updated with responsive sizes</span>
                      </motion.div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
